import React, {CSSProperties, useRef} from 'react';
import BackgroundTile from './BackgroundTile';
import {AssetMetaInterface, ObjectInterface} from '@taopix/taopix-core/dist/types/types';
import {AssetType, COLOUR_PREFIX} from '@taopix/taopix-core';
import {filterObject} from '../../Utils/Common';
import {CheckboxProps} from '@taopix/taopix-design-system/dist/types/Components/Checkbox/Checkbox';
import classNames from 'classnames';

export type Tile = {
  id: string;
  assetRef: string | number;
  src: string;
  categoryName: string;
  isColour: boolean;
  selected?: boolean;
  assetType: AssetType;
  asset: AssetMetaInterface | undefined;
};

type BackgroundsListProps = {
  backgrounds: AssetMetaInterface[];
  filterValues: ObjectInterface;
  orientation: 'vertical' | 'horizontal';
  columns: number;
  columnGap?: number;
  selectedBackgroundRef: string;
  onClick: (event: React.MouseEvent | React.ChangeEvent, selectedBackground: Tile) => void;
  onKeyDown?: (event: React.KeyboardEvent, selectedBackground: Tile) => void;
  backgroundColours: Record<string, string>;
  useHexCode?: boolean;
  parentSection: string;
  visible: boolean;
} & Pick<CheckboxProps, 'preventManualUncheck'>;

const BackgroundsList = ({
  backgrounds,
  filterValues,
  orientation,
  columns,
  columnGap,
  selectedBackgroundRef,
  onClick,
  onKeyDown,
  backgroundColours,
  useHexCode = false,
  parentSection,
  preventManualUncheck,
  visible,
}: BackgroundsListProps) => {
  let allBackgrounds: Tile[];

  if (backgrounds) {
    allBackgrounds = backgrounds.map(b => {
      return {
        id: String(b.id),
        assetRef: b.assetRef as string,
        src: b.thumbnailsrc,
        categoryName: b.categoryName as string,
        isColour: false,
        assetType: AssetType.Background,
        asset: b,
      };
    });
  } else {
    allBackgrounds = [];
  }

  if (backgroundColours) {
    for (const [, code] of Object.entries(backgroundColours)) {
      allBackgrounds.push({
        id: code,
        assetRef: `${COLOUR_PREFIX}${code}`,
        src: code,
        categoryName: '',
        isColour: true,
        assetType: AssetType.Background,
        asset: undefined,
      });
    }
  }

  // Tailwind classes for the UL
  const thumbnailListClasses = classNames(
    'flex',
    'w-[calc(100%+var(--gap))]',
    'self-start',
    'items-start',
    'flex-wrap',
    '-mt-[var(--gap)]',
    '-ml-[var(--gap)]'
  );

  const backgroundsListRef = useRef<HTMLUListElement>(null);

  const cssProperties = {'--columns': columns, '--gap': columnGap + 'rem'};

  if (!visible) {
    return <></>;
  }

  return (
    <ul
      key={`backgrounds-list-${parentSection}`}
      className={thumbnailListClasses}
      ref={backgroundsListRef}
      style={cssProperties as CSSProperties}
    >
      {allBackgrounds &&
        allBackgrounds
          ?.filter(p => filterObject(p, filterValues))
          .map((background, index) => {
            return (
              <BackgroundTile
                background={background}
                key={`background-${background.assetRef}-${index}`}
                parentSection={parentSection}
                checked={
                  useHexCode
                    ? background.src === selectedBackgroundRef
                    : background.assetRef === selectedBackgroundRef
                }
                onClick={onClick}
                onKeyDown={onKeyDown}
                preventManualUncheck={preventManualUncheck}
              />
            );
          })}
    </ul>
  );
};

export default BackgroundsList;
